import React, { useEffect, useMemo, useState } from "react";
import { Descriptions, Empty, Modal, Tag } from "antd";
import { CheckOutlined, GiftOutlined } from "@ant-design/icons";
import type { GroceryPackage, PackageItem } from "../types";

const PACKAGE_CURRENCY = "NGN";

export type PackageDetailModalProps = {
  open: boolean;
  groceryPackage?: GroceryPackage | null;
  onClose: () => void;
};

const groupItemsByCategory = (items: PackageItem[]) =>
  items.reduce<Record<string, PackageItem[]>>((groups, item) => {
    const category = item.category_name || "Uncategorized";
    return {
      ...groups,
      [category]: [...(groups[category] || []), item],
    };
  }, {});

export const PackageDetailModal: React.FC<PackageDetailModalProps> = ({
  open,
  groceryPackage,
  onClose,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const groupedItems = useMemo(
    () => groupItemsByCategory(groceryPackage?.items || []),
    [groceryPackage]
  );
  const imageUrl = groceryPackage?.image?.trim();
  const showImage = Boolean(imageUrl && !imageFailed);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  return (
    <Modal
      title="Package Details"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={720}
      destroyOnClose
      className="modal-round package-detail-modal"
    >
      {groceryPackage ? (
        <div className="package-detail">
          <div className="package-detail-hero">
            <div className="package-detail-image">
              {showImage ? (
                <img
                  src={imageUrl}
                  alt={groceryPackage.name}
                  onError={() => setImageFailed(true)}
                />
              ) : (
                <div className="package-card-placeholder">
                  <GiftOutlined />
                </div>
              )}
            </div>
            <div>
              <h2>{groceryPackage.name}</h2>
              <div className="package-detail-price">
                Market-based pricing
              </div>
              <Tag color={groceryPackage.is_active ? "success" : "default"}>
                {groceryPackage.is_active ? "Active" : "Inactive"}
              </Tag>
            </div>
          </div>

          <Descriptions column={1} bordered size="small" className="mb-3">
            <Descriptions.Item label="Billing Cycle">
              {groceryPackage.billing_cycle}
            </Descriptions.Item>
            <Descriptions.Item label="Total Items">
              {groceryPackage.items.length}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {groceryPackage.description}
            </Descriptions.Item>
          </Descriptions>

          <div className="package-detail-section">
            <h3>Benefits</h3>
            <ul className="package-detail-list">
              {groceryPackage.benefits.map((benefit) => (
                <li key={benefit}>
                  <CheckOutlined /> {benefit}
                </li>
              ))}
            </ul>
          </div>

          <div className="package-detail-section">
            <h3>Items</h3>
            {Object.entries(groupedItems).map(([category, items]) => (
              <div className="package-detail-category" key={category}>
                <div className="package-detail-category-title">{category}</div>
                <ul className="package-detail-list">
                  {items.map((item, index) => (
                    <li key={item.id || `${category}-${index}`}>
                      {item.product_name || `Product #${item.product_id}`},{" "}
                      {item.quantity} {item.unit}
                      {item.product_price ? ` • ${item.product_price}` : ""}
                      {item.product_unit ? ` / ${item.product_unit}` : ""}
                      {item.is_required ? "" : " (optional)"}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Empty description="No package found." />
      )}
    </Modal>
  );
};

export default PackageDetailModal;
